import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Alert,
  Chip,
} from '@mui/material';
import {
  ChevronLeft as PrevIcon,
  ChevronRight as NextIcon,
  ContentCopy as CopyIcon,
  SendRounded as SubmitIcon,
} from '@mui/icons-material';
import { getProjectDisplayKey } from '../utils/projectDisplay';

// Services where File Name / Description and Transactions are optional.
// Mirrors OPTIONAL_FILE_AND_TX_SERVICE_IDS in TimeTransactions.js and the backend.
const OPTIONAL_FILE_AND_TX_SERVICE_IDS = [30, 37];
const BATCH_SIZE = 200;
const MAX_HOURS_PER_DAY = 24; // applies per task per day AND to a person's whole day

// Task status is free text and stored inconsistently ("in_progress", "In Progress", "In-progress")
const isInProgress = (status) =>
  String(status || '').toLowerCase().replace(/[\s_-]+/g, ' ').trim() === 'in progress';

// ---- local-date helpers (never use toISOString — it shifts dates across timezones) ----
const pad = (n) => String(n).padStart(2, '0');
const toKey = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
const startOfWeek = (d) => { const x = startOfDay(d); const dow = (x.getDay() + 6) % 7; return addDays(x, -dow); }; // Monday

const getPeriodDays = (view, anchor) => {
  if (view === 'day') return [startOfDay(anchor)];
  if (view === 'week') { const s = startOfWeek(anchor); return Array.from({ length: 7 }, (_, i) => addDays(s, i)); }
  const first = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
  const count = new Date(anchor.getFullYear(), anchor.getMonth() + 1, 0).getDate();
  return Array.from({ length: count }, (_, i) => addDays(first, i));
};

const shiftAnchor = (view, anchor, dir) => {
  if (view === 'day') return addDays(anchor, dir);
  if (view === 'week') return addDays(anchor, 7 * dir);
  return new Date(anchor.getFullYear(), anchor.getMonth() + dir, 1);
};

const periodLabel = (view, days) => {
  const f = (d, o) => d.toLocaleDateString(undefined, o);
  if (view === 'day') return f(days[0], { weekday: 'long', month: 'short', day: 'numeric', year: 'numeric' });
  if (view === 'week') return `${f(days[0], { month: 'short', day: 'numeric' })} – ${f(days[6], { month: 'short', day: 'numeric', year: 'numeric' })}`;
  return f(days[0], { month: 'long', year: 'numeric' });
};

const fmtHours = (n) => (Math.round(n * 100) / 100).toString();

/**
 * Timesheet grid: rows = tasks assigned to the current user, columns = days.
 * Typing hours into a cell queues a NEW entry for that task/day; existing
 * logged hours are shown (grey) and never overwritten — edit/delete stays in
 * the List view.
 */
const TimesheetGrid = ({ user, tasks, entries, onSaved, notify }) => {
  const [view, setView] = useState('week');
  const [anchor, setAnchor] = useState(() => startOfDay(new Date()));
  const [cells, setCells] = useState({});       // `${taskId}|${date}` -> string hours
  const [rowMeta, setRowMeta] = useState({});   // taskId -> { fileName, transactions }
  const [cellErrors, setCellErrors] = useState({}); // `${taskId}|${date}` -> message from backend
  const [saving, setSaving] = useState(false);

  const todayKey = toKey(new Date());
  const days = useMemo(() => getPeriodDays(view, anchor), [view, anchor]);
  const dayKeys = useMemo(() => days.map(toKey), [days]);

  // Biometric scanned hours (first punch -> last punch) for the visible period.
  // Logging more than was scanned is allowed, but the day's total turns red and
  // managers see it in the Logged vs Scanned report.
  const [scan, setScan] = useState({ hasEmployeeId: false, byDate: {} });
  const periodStart = dayKeys[0];
  const periodEnd = dayKeys[dayKeys.length - 1];
  useEffect(() => {
    let cancelled = false;
    const params = new URLSearchParams({ startDate: periodStart, endDate: periodEnd });
    fetch(`${process.env.REACT_APP_API_URL}/time-transactions/scanned-hours?${params}`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (cancelled || !data) return;
        setScan({
          hasEmployeeId: !!data.hasEmployeeId,
          byDate: Object.fromEntries((data.days || []).map((d) => [d.date, d])),
        });
      })
      .catch(() => {}); // scanned hours are informational; the grid works without them
    return () => { cancelled = true; };
  }, [periodStart, periodEnd]);

  // The grid loads its own entries: the visible period plus the one before it
  // (for "Copy last …"). The List view's entries are filtered by its own date
  // range, which usually doesn't cover these dates.
  const [myEntries, setMyEntries] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const prevStart = useMemo(() => toKey(getPeriodDays(view, shiftAnchor(view, anchor, -1))[0]), [view, anchor]);
  useEffect(() => {
    if (!user?.id) return undefined;
    let cancelled = false;
    const params = new URLSearchParams({ startDate: prevStart, endDate: periodEnd, userId: user.id, page: 1, limit: 10000 });
    fetch(`${process.env.REACT_APP_API_URL}/time-transactions?${params}`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (cancelled) return;
        if (data) setMyEntries(Array.isArray(data) ? data : data.data || []);
      })
      .catch(() => {}); // fall back to the List view's entries below
    return () => { cancelled = true; };
  }, [prevStart, periodEnd, user?.id, reloadKey]);

  // Approval state for the visible week / month (not for the day view).
  const [approval, setApproval] = useState(null);
  const [submittingApproval, setSubmittingApproval] = useState(false);
  useEffect(() => {
    if (view === 'day') { setApproval(null); return undefined; }
    let cancelled = false;
    const params = new URLSearchParams({ periodType: view, periodStart });
    fetch(`${process.env.REACT_APP_API_URL}/timesheet-approvals/mine?${params}`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (!cancelled) setApproval(d); })
      .catch(() => {}); // approvals are an add-on; the grid works without them
    return () => { cancelled = true; };
  }, [view, periodStart, reloadKey]);

  const submitForApproval = async () => {
    setSubmittingApproval(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/timesheet-approvals/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
        body: JSON.stringify({ periodType: view, periodStart }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Could not submit');
      notify(data.autoApproved
        ? `Timesheet for ${approval?.label || 'this period'} submitted and approved.`
        : `Timesheet sent to ${data.submission?.approver?.name || 'your manager'} for approval.`);
      setReloadKey((k) => k + 1);
    } catch (err) {
      notify(err.message);
    } finally {
      setSubmittingApproval(false);
    }
  };

  // Hours already logged by this user, keyed by task + date. Merge what the
  // grid fetched with whatever the page already has, de-duplicated by id.
  const logged = useMemo(() => {
    const seen = new Set();
    const m = {};
    [...(myEntries || []), ...(entries || [])].forEach((e) => {
      const uid = e.userId ?? e.user?.id;
      if (uid !== user?.id) return;
      if (e.id != null) { if (seen.has(e.id)) return; seen.add(e.id); }
      const k = `${e.taskId}|${String(e.date).slice(0, 10)}`;
      m[k] = (m[k] || 0) + (parseFloat(e.hours) || 0);
    });
    return m;
  }, [myEntries, entries, user?.id]);

  // Most recent File Name / Transactions this user gave for each task, so a
  // copied week doesn't have to be re-described.
  const lastMeta = useMemo(() => {
    const m = {};
    [...(myEntries || []), ...(entries || [])]
      .filter((e) => (e.userId ?? e.user?.id) === user?.id)
      .sort((a, b) => String(b.date).localeCompare(String(a.date)))
      .forEach((e) => {
        if (m[e.taskId]) return;
        if (e.fileName || (e.transactions != null && e.transactions !== '')) {
          m[e.taskId] = { fileName: e.fileName || '', transactions: e.transactions != null ? String(e.transactions) : '' };
        }
      });
    return m;
  }, [myEntries, entries, user?.id]);

  // Hours already logged per day across ALL tasks (incl. ones not shown as rows),
  // so the daily 24h limit counts everything the person has logged.
  const loggedByDay = useMemo(() => {
    const m = {};
    Object.entries(logged).forEach(([k, h]) => {
      const date = k.split('|')[1];
      m[date] = (m[date] || 0) + h;
    });
    return m;
  }, [logged]);

  // Rows: tasks assigned to me whose status is "In Progress"
  const rows = useMemo(() => {
    return (tasks || [])
      .filter((t) => (t.assignees || []).some((a) => a.id === user?.id) && isInProgress(t.status))
      .map((t) => {
        const serviceId = t.Project?.serviceId;
        return {
          task: t,
          completed: t.Project?.status === 'completed',
          needsDetails: serviceId != null && !OPTIONAL_FILE_AND_TX_SERVICE_IDS.includes(Number(serviceId)),
        };
      })
      .sort((a, b) =>
        Number(a.completed) - Number(b.completed) ||
        (a.task.Project?.name || '').localeCompare(b.task.Project?.name || '') ||
        (a.task.title || '').localeCompare(b.task.title || ''));
  }, [tasks, user?.id]);

  const cellValue = (k) => parseFloat(cells[k]) || 0;
  const pendingKeys = Object.keys(cells).filter((k) => cellValue(k) > 0);

  const setCell = (k, v) => {
    if (v !== '' && !/^\d*\.?\d{0,2}$/.test(v)) return;
    if ((parseFloat(v) || 0) + (logged[k] || 0) > MAX_HOURS_PER_DAY) {
      notify(`A task can't have more than ${MAX_HOURS_PER_DAY} hours in one day${logged[k] ? ` (${fmtHours(logged[k])}h already logged)` : ''}.`);
      return;
    }
    setCells((prev) => ({ ...prev, [k]: v }));
    if (cellErrors[k]) setCellErrors((prev) => { const n = { ...prev }; delete n[k]; return n; });
  };

  const pendingForDay = (dk) => rows.reduce((sum, r) => sum + cellValue(`${r.task.id}|${dk}`), 0);
  const dayTotal = (dk) => (loggedByDay[dk] || 0) + pendingForDay(dk);
  const overLimitDays = dayKeys.filter((dk) => pendingForDay(dk) > 0 && dayTotal(dk) > MAX_HOURS_PER_DAY);
  const rowTotal = (taskId) => dayKeys.reduce((sum, dk) => {
    const k = `${taskId}|${dk}`;
    return sum + (logged[k] || 0) + cellValue(k);
  }, 0);
  const grandTotal = dayKeys.reduce((s, dk) => s + dayTotal(dk), 0);
  // Past days only — today is still in progress
  const overScanned = (dk) => {
    const s = scan.byDate[dk];
    return !!s && dk < todayKey && dayTotal(dk) > s.scannedHours;
  };
  const scannedTotal = dayKeys.reduce((sum, dk) => sum + (scan.byDate[dk]?.scannedHours || 0), 0);

  // Copy the previous period's logged hours into empty cells (same weekday / same day-of-month)
  const copyPrevious = () => {
    const prevDays = getPeriodDays(view, shiftAnchor(view, anchor, -1)).map(toKey);
    const next = { ...cells };
    const meta = { ...rowMeta };
    let copied = 0;
    let skippedFuture = 0;
    let hadPrevious = 0;
    rows.forEach(({ task, completed }) => {
      if (completed) return;
      dayKeys.forEach((dk, i) => {
        const src = prevDays[i];
        if (!src) return;
        const prevHours = logged[`${task.id}|${src}`];
        if (!prevHours) return;
        hadPrevious += 1;
        if (dk > todayKey) { skippedFuture += 1; return; }
        const k = `${task.id}|${dk}`;
        if (!next[k] && !logged[k]) {
          next[k] = fmtHours(prevHours);
          copied += 1;
          if (!meta[task.id] && lastMeta[task.id]) meta[task.id] = lastMeta[task.id];
        }
      });
    });
    setCells(next);
    setRowMeta(meta);
    if (copied) {
      notify(`Copied ${copied} cell(s) from the previous ${view}.${skippedFuture ? ` ${skippedFuture} future day(s) skipped.` : ''} Review, then save.`);
    } else if (hadPrevious && skippedFuture) {
      notify(`Nothing to copy yet: last ${view}'s hours fall on days that haven't happened yet this ${view}.`);
    } else if (hadPrevious) {
      notify(`Nothing to copy: those days already have hours logged.`);
    } else if (myEntries == null) {
      notify(`Still loading last ${view}'s hours, try again in a moment.`);
    } else {
      notify(`Nothing to copy: no hours were logged last ${view} on your in-progress tasks.`);
    }
  };

  const handleSave = async () => {
    // Validate
    const problems = [];
    const payload = [];
    pendingKeys.forEach((k) => {
      const [taskIdStr, date] = k.split('|');
      const taskId = Number(taskIdStr);
      const row = rows.find((r) => r.task.id === taskId);
      if (!row) return;
      const hours = cellValue(k);
      if (hours + (logged[k] || 0) > MAX_HOURS_PER_DAY) problems.push(`${row.task.title} on ${date}: more than ${MAX_HOURS_PER_DAY} hours for this task`);
      if (date > todayKey) problems.push(`${row.task.title} on ${date}: future date`);
      const meta = rowMeta[taskId] || {};
      if (row.needsDetails) {
        if (!meta.fileName || !meta.fileName.trim()) problems.push(`${row.task.title}: File Name / Description is required for this service`);
        if (meta.transactions === undefined || meta.transactions === '') problems.push(`${row.task.title}: Transactions is required for this service`);
      }
      payload.push({
        _key: k,
        userId: user.id,
        taskId,
        date,
        hours,
        transactions: meta.transactions !== undefined && meta.transactions !== '' ? parseInt(meta.transactions, 10) : 0,
        transactionType: null,
        fileName: meta.fileName && meta.fileName.trim() ? meta.fileName.trim() : null,
      });
    });
    overLimitDays.forEach((dk) => {
      problems.unshift(`${dk}: ${fmtHours(dayTotal(dk))}h total — a day can't exceed ${MAX_HOURS_PER_DAY} hours`);
    });
    const unique = [...new Set(problems)];
    if (unique.length) { notify(unique.slice(0, 3).join(' · ') + (unique.length > 3 ? ` (+${unique.length - 3} more)` : '')); return; }
    if (!payload.length) return;

    setSaving(true);
    const savedKeys = [];
    const errors = {};
    try {
      for (let offset = 0; offset < payload.length; offset += BATCH_SIZE) {
        const batch = payload.slice(offset, offset + BATCH_SIZE);
        const res = await fetch(`${process.env.REACT_APP_API_URL}/time-transactions/bulk`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
          body: JSON.stringify({ entries: batch.map(({ _key, ...e }) => e) }),
        });
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(body.message || `Save failed (HTTP ${res.status})`);
        }
        const data = await res.json();
        const failedIdx = new Map((data.failed || []).map((f) => [f.index, f.error]));
        batch.forEach((e, i) => {
          if (failedIdx.has(i)) errors[e._key] = failedIdx.get(i) || 'Not saved';
          else savedKeys.push(e._key);
        });
      }
    } catch (err) {
      notify(`${err.message}${savedKeys.length ? ` — ${savedKeys.length} entr${savedKeys.length === 1 ? 'y was' : 'ies were'} saved before the error.` : ''}`);
    } finally {
      // Saved cells are now "logged" — clear them from the editable state
      setCells((prev) => { const n = { ...prev }; savedKeys.forEach((k) => delete n[k]); return n; });
      setCellErrors(errors);
      if (savedKeys.length) {
        const failedCount = Object.keys(errors).length;
        notify(`Saved ${savedKeys.length} entr${savedKeys.length === 1 ? 'y' : 'ies'}${failedCount ? `, ${failedCount} failed (highlighted in red)` : ''}.`);
        setReloadKey((k) => k + 1);
        onSaved();
      }
      setSaving(false);
    }
  };

  const isMonth = view === 'month';
  const colWidth = isMonth ? 52 : view === 'day' ? 140 : 84;
  const headSx = { fontWeight: 600, whiteSpace: 'nowrap', bgcolor: 'background.paper' };
  const stickyFirst = { position: 'sticky', left: 0, zIndex: 2, bgcolor: 'background.paper', minWidth: 260, maxWidth: 320 };
  // Light blue tint for today's column (opaque in the header so sticky rows don't show through)
  const todayHeadBg = (t) => (t.palette.mode === 'dark' ? '#1c2a40' : '#eaf2ff');
  const todayCellBg = (t) => (t.palette.mode === 'dark' ? 'rgba(59,130,246,0.10)' : '#f3f8ff');

  return (
    <Box>
      {/* Toolbar */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', mb: 2 }}>
        <ToggleButtonGroup size="small" exclusive value={view} onChange={(_, v) => v && setView(v)}>
          <ToggleButton value="day">Day</ToggleButton>
          <ToggleButton value="week">Week</ToggleButton>
          <ToggleButton value="month">Month</ToggleButton>
        </ToggleButtonGroup>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton size="small" onClick={() => setAnchor(shiftAnchor(view, anchor, -1))} aria-label="Previous period"><PrevIcon /></IconButton>
          <Button size="small" onClick={() => setAnchor(startOfDay(new Date()))} sx={{ textTransform: 'none' }}>Today</Button>
          <IconButton size="small" onClick={() => setAnchor(shiftAnchor(view, anchor, 1))} aria-label="Next period"><NextIcon /></IconButton>
        </Box>
        <Box sx={{ fontWeight: 600 }}>{periodLabel(view, days)}</Box>
        {approval && (() => {
          // Approval status of the visible period: a chip, and a submit button when it's the person's move.
          const sub = approval.submission;
          const covered = approval.coveredByMonth;
          const status = covered ? 'approved' : sub?.status;
          const chip = {
            approved: { label: covered ? 'Approved (whole month)' : sub?.autoApproved ? 'Submitted' : `Approved by ${sub?.decider?.name || 'your manager'}`, color: 'success' },
            pending: { label: `Waiting for ${sub?.approver?.name || 'your manager'}`, color: 'warning' },
            rejected: { label: `Sent back${sub?.comment ? `: ${sub.comment}` : ''}`, color: 'error' },
            changed: { label: 'Changed since approval — resubmit', color: 'warning' },
          }[status];
          const canSubmit = !covered && (!sub || ['rejected', 'changed'].includes(sub.status));
          const unsaved = pendingKeys.length > 0;
          const noHours = !dayKeys.some((dk) => (loggedByDay[dk] || 0) > 0);
          const title = approval.needsManager
            ? 'No manager is set for you in FlatForm yet. Ask an admin to set "Reports to" on your user.'
            : unsaved ? 'Save your entries first'
              : noHours ? `No hours logged this ${view} yet`
                : approval.autoApprove ? 'Marks this timesheet as submitted' : 'Sends it to your manager for approval';
          return (
            <>
              {chip && <Chip size="small" label={chip.label} color={chip.color} variant="outlined" sx={{ maxWidth: 360 }} />}
              {canSubmit && (
                <Tooltip title={title}>
                  <span>
                    <Button size="small" variant="outlined" startIcon={<SubmitIcon />} onClick={submitForApproval}
                      disabled={submittingApproval || saving || unsaved || noHours || approval.needsManager}
                      sx={{ textTransform: 'none' }}>
                      {submittingApproval ? 'Submitting…' : sub ? 'Resubmit' : 'Submit for approval'}
                    </Button>
                  </span>
                </Tooltip>
              )}
            </>
          );
        })()}
        <Box sx={{ flexGrow: 1 }} />
        <Button size="small" variant="outlined" startIcon={<CopyIcon />} onClick={copyPrevious} disabled={saving}
          sx={{ textTransform: 'none', color: 'text.secondary', borderColor: '#e5e7eb' }}>
          Copy last {view}
        </Button>
        <Button size="small" variant="contained" onClick={handleSave} disabled={saving || pendingKeys.length === 0 || overLimitDays.length > 0} sx={{ textTransform: 'none' }}>
          {saving ? 'Saving…' : `Save ${pendingKeys.length} entr${pendingKeys.length === 1 ? 'y' : 'ies'}`}
        </Button>
      </Box>

      {overLimitDays.length > 0 && (
        <Alert severity="error" sx={{ mb: 1.5 }}>
          {overLimitDays.map((dk) => {
            const d = days[dayKeys.indexOf(dk)];
            return `${d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })}: ${fmtHours(dayTotal(dk))}h`;
          }).join(' · ')}
          {' '}— a day can't exceed {MAX_HOURS_PER_DAY} hours in total (including hours already logged). Reduce those days to save.
        </Alert>
      )}

      {rows.length === 0 ? (
        <Alert severity="info">You have no tasks In Progress. Tasks assigned to you appear here once their status is set to In Progress.</Alert>
      ) : (
        <TableContainer sx={{ border: '1px solid #e5e7eb', borderRadius: 2, maxHeight: 560 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ ...headSx, ...stickyFirst, zIndex: 3 }}>Task</TableCell>
                {days.map((d, i) => {
                  const weekend = d.getDay() === 0 || d.getDay() === 6;
                  const isToday = dayKeys[i] === todayKey;
                  return (
                    <TableCell key={dayKeys[i]} align="center"
                      sx={{ ...headSx, minWidth: colWidth, px: 0.5, color: isToday ? 'primary.main' : weekend ? 'text.disabled' : undefined, ...(isToday && { bgcolor: todayHeadBg }) }}>
                      <Box sx={{ fontSize: '0.7rem', textTransform: 'uppercase' }}>{d.toLocaleDateString(undefined, { weekday: isMonth ? 'narrow' : 'short' })}</Box>
                      <Box>{d.getDate()}</Box>
                    </TableCell>
                  );
                })}
                <TableCell align="right" sx={{ ...headSx, minWidth: 64 }}>Total</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map(({ task, completed, needsDetails }) => {
                const meta = rowMeta[task.id] || {};
                return (
                  <TableRow key={task.id} sx={{ opacity: completed ? 0.55 : 1 }}>
                    <TableCell sx={{ ...stickyFirst, verticalAlign: 'top' }}>
                      <Box sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                        {getProjectDisplayKey(task.Project) || task.Project?.name}
                        {task.Project?.name && getProjectDisplayKey(task.Project) ? ` · ${task.Project.name}` : ''}
                      </Box>
                      <Box sx={{ fontWeight: 500, fontSize: '0.875rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={task.title}>
                        {task.title}
                      </Box>
                      {completed && <Box sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>Project completed — no new entries</Box>}
                      {needsDetails && !completed && (
                        <Box sx={{ display: 'flex', gap: 0.75, mt: 0.75 }}>
                          <TextField size="small" placeholder="File / description *" value={meta.fileName || ''}
                            onChange={(e) => setRowMeta((p) => ({ ...p, [task.id]: { ...p[task.id], fileName: e.target.value } }))}
                            inputProps={{ style: { fontSize: '0.75rem', padding: '4px 6px' } }} sx={{ flex: 2 }} />
                          <TextField size="small" placeholder="Tx *" value={meta.transactions ?? ''}
                            onChange={(e) => /^\d*$/.test(e.target.value) && setRowMeta((p) => ({ ...p, [task.id]: { ...p[task.id], transactions: e.target.value } }))}
                            inputProps={{ style: { fontSize: '0.75rem', padding: '4px 6px' }, inputMode: 'numeric' }} sx={{ flex: 1 }} />
                        </Box>
                      )}
                    </TableCell>
                    {dayKeys.map((dk, i) => {
                      const k = `${task.id}|${dk}`;
                      const already = logged[k] || 0;
                      const future = dk > todayKey;
                      const weekend = days[i].getDay() === 0 || days[i].getDay() === 6;
                      const err = cellErrors[k];
                      const field = (
                        <TextField
                          size="small"
                          value={cells[k] ?? ''}
                          onChange={(e) => setCell(k, e.target.value)}
                          disabled={completed || future || saving}
                          placeholder={already ? fmtHours(already) : ''}
                          error={!!err}
                          inputProps={{ inputMode: 'decimal', style: { textAlign: 'center', padding: '6px 2px', fontSize: '0.8125rem' }, 'aria-label': `${task.title} ${dk}` }}
                          sx={{ width: '100%', '& .MuiOutlinedInput-root': { bgcolor: weekend ? 'rgba(0,0,0,0.02)' : undefined } }}
                        />
                      );
                      return (
                        <TableCell key={dk} align="center" sx={{ px: 0.5, py: 0.75, ...(dk === todayKey && { bgcolor: todayCellBg }) }}>
                          {err ? <Tooltip title={err}>{field}</Tooltip>
                            : already ? <Tooltip title={`${fmtHours(already)}h already logged — typing adds a new entry`}>{field}</Tooltip>
                            : field}
                        </TableCell>
                      );
                    })}
                    <TableCell align="right" sx={{ fontWeight: 600 }}>{fmtHours(rowTotal(task.id)) || '0'}</TableCell>
                  </TableRow>
                );
              })}
              {scan.hasEmployeeId && (
                <TableRow>
                  <TableCell sx={{ ...stickyFirst, color: 'text.secondary', fontSize: '0.8125rem' }}>
                    <Tooltip title="Time from your first to last biometric scan of the day">
                      <span>Scanned in</span>
                    </Tooltip>
                  </TableCell>
                  {dayKeys.map((dk) => {
                    const s = scan.byDate[dk];
                    return (
                      <TableCell key={dk} align="center" sx={{ color: 'text.secondary', fontSize: '0.8125rem', ...(dk === todayKey && { bgcolor: todayCellBg }) }}>
                        {s ? (
                          <Tooltip title={`In ${s.firstIn} · Out ${s.lastOut}${s.missingOut ? ' (no out punch)' : ''}${dk === todayKey ? ' · so far today' : ''}`}>
                            <span>{fmtHours(s.scannedHours)}</span>
                          </Tooltip>
                        ) : dk <= todayKey ? '—' : ''}
                      </TableCell>
                    );
                  })}
                  <TableCell align="right" sx={{ color: 'text.secondary', fontSize: '0.8125rem' }}>{fmtHours(scannedTotal)}</TableCell>
                </TableRow>
              )}
              <TableRow>
                <TableCell sx={{ ...stickyFirst, fontWeight: 700 }}>Daily total</TableCell>
                {dayKeys.map((dk) => {
                  const t = dayTotal(dk);
                  const over = overScanned(dk);
                  const cell = (
                    <TableCell key={dk} align="center" sx={{ fontWeight: 700, color: t > MAX_HOURS_PER_DAY || over ? 'error.main' : undefined, ...(over && { bgcolor: (th) => (th.palette.mode === 'dark' ? 'rgba(239,68,68,0.12)' : '#fef2f2') }), ...(dk === todayKey && { bgcolor: todayCellBg }) }}>
                      {t ? fmtHours(t) : ''}
                    </TableCell>
                  );
                  return over ? (
                    <Tooltip key={dk} title={`${fmtHours(t)}h logged is more than the ${fmtHours(scan.byDate[dk].scannedHours)}h scanned in. You can still save; your manager will see the difference.`}>
                      {cell}
                    </Tooltip>
                  ) : cell;
                })}
                <TableCell align="right" sx={{ fontWeight: 700 }}>{fmtHours(grandTotal)}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      )}
      <Box sx={{ mt: 1, fontSize: '0.75rem', color: 'text.secondary' }}>
        Only tasks that are In Progress are shown. Grey numbers are hours already logged; typing in a cell adds a new entry for that day. Max {MAX_HOURS_PER_DAY}h per task per day and {MAX_HOURS_PER_DAY}h per day in total (including hours logged on other tasks). To change or delete existing entries, use the List view.
        {scan.hasEmployeeId && ' A daily total shown in red is more than your scanned-in time for that day — you can still save it, and your manager will see the difference.'}
      </Box>
    </Box>
  );
};

export default TimesheetGrid;
